import { captureException } from '@sentry/react';
import { addDays, addHours } from 'date-fns';
import { addBusinessHours, getUTCMidDay } from 'utils/date';
import { SignupError } from 'utils/errors/debuggableError';
import { SignupAdultConcern, SignupPediatricConcern } from '../misc/communicationConcerns';
import { SignupPaymentMethod } from '../schemas/filtersSchema';

/** Evaluations can be scheduled up to this date in the future */
export const MAX_DAYS_FOR_FIRST_EVALUATION = 21;

/** How many days from today to wait before first day that can be picked for evaluation */
export const DEFAULT_EVALUATION_OFFSETS = {
  INSURANCE: 3,
  PRIVATE_PAY: 3,
  DEFAULT: 1,
};

/** AAC concerns that require extended lead time */
const AAC_CONCERNS: (SignupPediatricConcern | SignupAdultConcern)[] = [
  'Assisted communication device',
  'Using a device or speech app for communication',
];

/** Lead time in days for AAC concerns */
const AAC_EVALUATION_LEAD_TIME_DAYS = 10;

/** Using current date, returns the range of dates that can be picked for evaluation */
export const getEvaluationSchedulingWindow = (
  paymentMethod: SignupPaymentMethod,
  clinicalConcernKeys: string[],
): {
  from: Date;
  to: Date;
} => {
  const today = getUTCMidDay();

  let from: Date;
  const to = addDays(today, MAX_DAYS_FOR_FIRST_EVALUATION);

  const hasAACConcern = clinicalConcernKeys.some(key =>
    AAC_CONCERNS.includes(key as SignupAdultConcern | SignupPediatricConcern),
  );

  if (hasAACConcern) {
    from = addDays(today, AAC_EVALUATION_LEAD_TIME_DAYS);

    return {
      from,
      to,
    };
  }

  switch (paymentMethod.type) {
    case 'privatePay':
      from = addDays(today, DEFAULT_EVALUATION_OFFSETS.PRIVATE_PAY);
      break;
    case 'insurance': {
      const insurance = paymentMethod.insuranceData;
      const leadTimeInHours = insurance.evalLeadTime ?? DEFAULT_EVALUATION_OFFSETS.INSURANCE * 24;

      const countBusinessDaysOnly = insurance.evalAuthorizationRequired || insurance.physicianOrderRequired;

      if (countBusinessDaysOnly) {
        from = addBusinessHours(today, leadTimeInHours);
      } else {
        from = addHours(today, leadTimeInHours);
      }
      break;
    }
    default:
      captureException(
        new SignupError(
          `Reached end of getEvaluationTimeFrames without a proper return, defaulting to private pay offset`,
          {
            debugData: {
              paymentMethod,
            },
          },
        ),
      );
      from = addDays(today, DEFAULT_EVALUATION_OFFSETS.PRIVATE_PAY);
      break;
  }

  return {
    from,
    to,
  };
};
